/**
 * @file Musique d'ambiance 100% synthetisee (aucun fichier, aucune licence) : boucle douce en fa majeur, 96 BPM.
 * Progression I-V-vi-IV (2 mesures par accord), nappe tres feutree, basse ronde sur les temps 1 et 3, petite melodie
 * pentatonique en croches, re-tiree a chaque passage (jamais deux fois la meme) avec des silences pour respirer.
 * La composition (`composeBar`) est pure et testee ; `ProceduralMusic` ne fait que planifier les notes (lookahead WebAudio).
 */

export interface MusicNote {
  /** Debut en temps (noires) depuis le debut de la mesure. */
  beat: number;
  /** Duree en temps. */
  len: number;
  /** Note MIDI (69 = la 440). */
  midi: number;
  voice: 'pad' | 'bass' | 'lead';
  /** Velocite relative 0..1. */
  vel: number;
}

export const MUSIC_BPM = 96;
export const BEATS_PER_BAR = 4;
/** Mesures par passage de la boucle (4 accords x 2 mesures). */
export const BARS_PER_LOOP = 8;

/** Accords (MIDI, octave de la nappe) : F, C, Dm, Bb. */
const CHORDS: readonly (readonly number[])[] = [
  [65, 69, 72],
  [60, 64, 67],
  [62, 65, 69],
  [58, 62, 65],
];
/** Fondamentales de basse (octave 2). */
const ROOTS = [41, 36, 38, 34];
/** Pentatonique de fa majeur (F G A C D) sur deux octaves, registre aigu de la melodie. */
const PENTA = [77, 79, 81, 84, 86, 89, 91, 93];

/** Aleatoire injecte (Math.random en jeu, generateur graine dans les tests). */
export type Rand = () => number;

/** Notes d'une mesure de la boucle (`bar` = index de mesure, quelconque : on prend bar % BARS_PER_LOOP). */
export const composeBar = (bar: number, rand: Rand): MusicNote[] => {
  const b = ((bar % BARS_PER_LOOP) + BARS_PER_LOOP) % BARS_PER_LOOP;
  const ci = Math.floor(b / 2);
  const chord = CHORDS[ci] ?? CHORDS[0]!;
  const root = ROOTS[ci] ?? ROOTS[0]!;
  const notes: MusicNote[] = [];
  // nappe : tenue sur toute la mesure (re-attaque douce a chaque mesure)
  for (const m of chord) notes.push({ beat: 0, len: BEATS_PER_BAR, midi: m, voice: 'pad', vel: 0.7 });
  // basse : temps 1 et 3 (quinte sur le 3 une mesure sur deux)
  notes.push({ beat: 0, len: 1.6, midi: root, voice: 'bass', vel: 1 });
  notes.push({ beat: 2, len: 1.6, midi: b % 2 === 1 ? root + 7 : root, voice: 'bass', vel: 0.85 });
  // melodie : croches, notes de la pentatonique proches de l'accord, silences frequents ; derniere mesure du passage plus calme
  const density = b === BARS_PER_LOOP - 1 ? 0.25 : b % 2 === 0 ? 0.55 : 0.4;
  let idx = Math.floor(rand() * PENTA.length);
  for (let step = 0; step < BEATS_PER_BAR * 2; step++) {
    if (rand() > density) continue;
    // marche de -2..+2 degres : contour chantant plutot que sauts
    idx = Math.min(PENTA.length - 1, Math.max(0, idx + Math.floor(rand() * 5) - 2));
    const midi = PENTA[idx]!;
    notes.push({ beat: step / 2, len: rand() < 0.25 ? 1 : 0.5, midi, voice: 'lead', vel: step % 2 === 0 ? 0.9 : 0.65 });
  }
  return notes;
};

const midiHz = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);

/** Gains crete par voix (avant le volume musique) : volontairement bas, la musique reste sous les effets sonores. */
const VOICE: Record<MusicNote['voice'], { type: OscillatorType; gain: number; attack: number; release: number; filter: number }> = {
  pad: { type: 'triangle', gain: 0.045, attack: 0.5, release: 0.6, filter: 900 },
  bass: { type: 'triangle', gain: 0.165, attack: 0.02, release: 0.25, filter: 500 },
  lead: { type: 'sine', gain: 0.105, attack: 0.01, release: 0.35, filter: 2400 },
};

/** Planification : on programme les mesures jusqu'a LOOKAHEAD secondes en avance, verifie toutes les TICK_MS. */
const LOOKAHEAD = 0.8;
const TICK_MS = 200;

export class ProceduralMusic {
  private timer: ReturnType<typeof setInterval> | null = null;
  private nextBarTime = 0;
  private bar = 0;

  constructor(
    private readonly ctx: AudioContext,
    private readonly out: AudioNode,
    private readonly rand: Rand = Math.random,
  ) {}

  get playing(): boolean {
    return this.timer !== null;
  }

  start(): void {
    if (this.timer !== null) return;
    this.nextBarTime = this.ctx.currentTime + 0.15;
    this.schedule();
    this.timer = setInterval(() => this.schedule(), TICK_MS);
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  private schedule(): void {
    const barSec = (60 / MUSIC_BPM) * BEATS_PER_BAR;
    // contexte reste suspendu longtemps (onglet cache) : on repart de maintenant plutot que de rattraper
    if (this.nextBarTime < this.ctx.currentTime - barSec) this.nextBarTime = this.ctx.currentTime + 0.1;
    while (this.nextBarTime < this.ctx.currentTime + LOOKAHEAD) {
      for (const n of composeBar(this.bar, this.rand)) this.note(n, this.nextBarTime);
      this.nextBarTime += barSec;
      this.bar++;
    }
  }

  private note(n: MusicNote, barStart: number): void {
    const ctx = this.ctx;
    const v = VOICE[n.voice];
    const beat = 60 / MUSIC_BPM;
    const t0 = barStart + n.beat * beat;
    const tOff = t0 + n.len * beat;
    const tEnd = tOff + v.release;
    const osc = ctx.createOscillator();
    osc.type = v.type;
    osc.frequency.value = midiHz(n.midi);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = v.filter;
    const env = ctx.createGain();
    const peak = v.gain * n.vel;
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(peak, t0 + v.attack);
    // tenue legerement decroissante puis extinction (pluck pour la melodie, nappe plus plate)
    env.gain.setTargetAtTime(peak * (n.voice === 'pad' ? 0.8 : 0.35), t0 + v.attack, n.voice === 'pad' ? 1.2 : 0.18);
    env.gain.setTargetAtTime(0, tOff, v.release / 3);
    osc.connect(filter);
    filter.connect(env);
    env.connect(this.out);
    osc.start(t0);
    osc.stop(tEnd + 0.05);
    osc.onended = () => {
      osc.disconnect();
      filter.disconnect();
      env.disconnect();
    };
  }
}
